import axios, { type AxiosRequestConfig } from "axios";
import HelperService from "../services/helper-service";
import { OFFLINE_REDIRECT_CANCEL } from "../shared/utilty/constants";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import { ReactSystemFunctions } from "../shared/utilty/react-system-functions";
import { getCookie, deleteCookie, setCookie } from "./cookie-utils";
import LocalStorageService from "./local-storage-service";

const isNetworkLevelError = (error: { response?: unknown; code?: string; message?: string }): boolean =>
  !error.response &&
  (error.code === "ERR_NETWORK" ||
    error.code === "ERR_INTERNET_DISCONNECTED" ||
    error.code === "ECONNREFUSED" ||
    error.message === "Network Error");

declare const window: any;

axios.defaults.withCredentials = true;

const backendUrl = KuikaAppManager.GetBackendUrl();
let isTokenRefreshing = false;
const localStorageService = LocalStorageService.getService();

interface FailedQueueItem {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

interface AuthRetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
  __retryCount?: number;
}

interface RefreshFailureRedirectOptions {
  redirectUri?: string;
  skipWhenAlreadyOnLogin?: boolean;
}

let failedQueue: FailedQueueItem[] = [];

const MAX_RETRIES: number = 3;
const RETRY_DELAY: number = 10000;



axios.interceptors.response.use(undefined, async (error) => {
  // Skip if request was cancelled (e.g., offline redirect)
  if (axios.isCancel(error) || !error.config) {
    return Promise.reject(error);
  }

  // Skip retry for rate limiting responses
  if (error.response && error.response.status === 429) {
    return Promise.reject(error);
  }

  const config = error.config;
  config.__retryCount = config.__retryCount || 0;
  const isNetworkError = isNetworkLevelError(error);
  if (isNetworkError) {
    ReactSystemFunctions.markNetworkError();
  }

  // Fail fast when offline so callers fall back to their caches instead of
  // burning 30s on retries. `isOnline()` reflects observed traffic, not just
  // navigator.onLine, so DevTools-Offline + ERR_INTERNET_DISCONNECTED counts.
  const isOnline: boolean = await ReactSystemFunctions.isOnline();
  if (!isOnline) {
    
    return Promise.reject(error);
  }

  if (isNetworkError && (MAX_RETRIES === -1 || config.__retryCount < MAX_RETRIES)) {
    config.__retryCount += 1;

    console.log(`Retrying request (${config.__retryCount}/${MAX_RETRIES}): ${config.url}`, {
      method: config.method,
      errorCode: error.code,
      errorMessage: error.message
    });

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));

    return axios(config);
  }

  return Promise.reject(error);
});

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

const isRefreshTokenRequest = (config: AuthRetryConfig | undefined): boolean => {
  return config?.url?.includes("refreshtoken") === true;
};

const clearRefreshStateAndRedirect = (error: unknown, options: RefreshFailureRedirectOptions = {}): Promise<never> => {
  isTokenRefreshing = false;
  processQueue(error);
  deleteCookie("is_logged_in");
  deleteCookie("session_id");
  deleteCookie("csrf_token");
  window.userInfo = undefined;

  if (options.skipWhenAlreadyOnLogin === true && HelperService.getIsInLoginPage()) {
    return Promise.reject(error);
  }

  if (options.redirectUri !== undefined) {
    HelperService.gotoLogin(options.redirectUri);
  } else {
    HelperService.gotoLogin();
  }

  return Promise.reject(error);
};

// Add a request interceptor
axios.interceptors.request.use(
  async (config) => {
    

    const urlItems = document.location.pathname.substring(1).split(/\//);
    config.withCredentials = true;
    const language = localStorage.getItem("ml");
    if (language && language.length) {
      config.headers.cultureName = language;
    }
    // CSRF token: echo the non-HttpOnly csrf_token cookie in the X-CSRF-Token
    // header on every request. The backend CsrfMiddleware compares the two
    // and rejects state-changing cookie-auth requests where they don't match,
    // which is what blocks a cross-site attacker who can force the cookie to
    // be sent but cannot read its value to put in the header.
    const csrfToken: string | null = getCookie("csrf_token");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    // Domain headers
    if (urlItems[0] && urlItems[0] !== "") {
      config.headers.domainName = urlItems[0];
    }
    if (window.kuika && window.kuika?.project && window.kuika?.project.domainId) {
      config.headers.domainId = window.kuika?.project.domainId;
    }
    const nextAudit = KuikaAppManager.consumeNextRequestAudit();
    if (nextAudit?.key) {
      config.headers["x-audit-key"] = nextAudit.key;
      config.headers["x-audit-name"] = nextAudit.name;
      if (nextAudit.source) {
        config.headers["x-audit-source"] = nextAudit.source;
      }
      if (nextAudit.data !== undefined) {
        config.headers["x-audit-data"] = JSON.stringify(nextAudit.data);
      }
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Add a response interceptor
axios.interceptors.response.use(
  async (response) => {
    ReactSystemFunctions.markNetworkSuccess();
    return response;
  },
  async (error) => {
    const originalRequest: AuthRetryConfig | undefined = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (isRefreshTokenRequest(originalRequest)) {
      if (isTokenRefreshing === false) {
        deleteCookie("is_logged_in");
        deleteCookie("session_id");
        deleteCookie("csrf_token");
        window.userInfo = undefined;
        HelperService.gotoLogin();
      }
      return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest.url === `${backendUrl}/auth/login`) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (isTokenRefreshing === false) {
        isTokenRefreshing = true;
        const isLoggedIn = getCookie("is_logged_in") === "1";

        if (!isLoggedIn) {
          const redirectUri =
            window.location.pathname +
            window.location.search +
            (window.location.hash != undefined ? window.location.hash : "");
          return clearRefreshStateAndRedirect(new Error("No active login cookie"), {
            redirectUri,
            skipWhenAlreadyOnLogin: true
          });
        }

        try {
          const res = await axios.post(`${backendUrl}/auth/refreshtoken`, {}, { withCredentials: true });
          isTokenRefreshing = false;
          if (res && res.status === 200) {
            // Server rotates the HttpOnly auth cookies + the csrf_token cookie on
            // the API host automatically. The csrf_token mirror on the frontend
            // host has to be re-written here — when SPA and API live on
            // different subdomains, document.cookie can't see the API-host
            // cookie, so without this write the next request still carries the
            // pre-refresh CSRF value in the header while the server side has
            // already rotated. CsrfMiddleware then rejects with 403 even
            // though refresh succeeded.
            const newCsrfToken: string | undefined = res.data?.csrfToken;
            if (newCsrfToken) {
              const THIRTY_DAYS: number = 30 * 24 * 60 * 60;
              setCookie("csrf_token", newCsrfToken, THIRTY_DAYS);
            }
            processQueue(null, res.data.token);
            return axios(originalRequest);
          }

          // Anything 2xx-but-not-200 (e.g. 204 from a proxy, 302 from an
          // auth gateway) means the refresh didn't yield usable tokens.
          const refreshError = new Error("Refresh token endpoint returned non-200");
          return clearRefreshStateAndRedirect(refreshError);
        } catch (e) {
          return clearRefreshStateAndRedirect(e);
        }
      }
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          // Cookie is sent automatically — no need to set Authorization header
          return axios(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    return Promise.reject(error);
  }
);
